import { useTranslation } from 'react-i18next';
import { type ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { useRuleExtractionTracking } from '@/rules-management/primary/RuleExtractionTrackingContext.ts';
import {
  IpponBadge,
  IpponButton,
  IpponCard,
  IpponContainer,
  IpponField,
  IpponHSpace,
  IpponHSpaceSlot,
  IpponInputSearch,
  IpponIon,
  IpponLabel,
  IpponSingleCombobox,
  IpponText,
  IpponTitle,
  IpponVSpace,
} from '@ippon-ui/react';
import { Optional, type Result } from '@/generic/functional';
import { LoaderComponent, LoadingErrorComponent, useLoader } from '@/generic/loader';
import { type Page, Pagination, PaginationComponent } from '@/generic/pagination';
import { inject } from '@/rules-management/injections.ts';
import { RULES_REPOSITORY } from '@/rules-management/RulesManagementKeys.ts';
import {
  RuleDetailsComponent,
  RuleHeadingComponent,
  type Rule,
  RuleStatus,
  RulesFilter,
  RulesScope,
  Severity,
  useSeverityText,
} from '@/rule';
import {
  initAccount,
  type OrganizationId,
  type User,
  type WithoutOrganization,
} from '@/supporting/account';
import { RuleLoadingComponent } from '@/rules-management/primary/RuleLoadingComponent.tsx';
import { RulesImportTriggerComponent } from '@/rules-management/primary/RulesImportTriggerComponent.tsx';
import { useCanManageRules } from '@/rules-management/primary/UseCanManageRules.ts';

const RuleStatusActionComponent = ({ rule, onChanged }: { rule: Rule; onChanged: () => void }) => {
  const { t } = useTranslation('rules-management');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const isActive = rule.status === RuleStatus.ACTIVE;
  const nextStatus = isActive ? RuleStatus.INACTIVE : RuleStatus.ACTIVE;

  const toggleStatus = async () => {
    setPending(true);
    setFailed(false);
    try {
      await inject(RULES_REPOSITORY).updateStatus(rule.id, nextStatus);
      onChanged();
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  };

  return (
    <IpponHSpace gap={8} align="middle">
      {failed && (
        <IpponText
          variant="body"
          size="small"
          color="error-primary"
          dataSelector="rules-management.rules.rule.status-error"
        >
          {t('rules-management.rules.rule.status-error')}
        </IpponText>
      )}
      {isActive ? (
        <IpponButton
          color="neutral"
          variant="text"
          disabled={pending}
          onClick={toggleStatus}
          iconLeft={{ name: 'pause', variant: 'outline' }}
          label={t('rules-management.rules.rule.deactivate')}
          dataSelector="rules-management.rules.rule.deactivate"
        />
      ) : (
        <IpponButton
          color="success"
          variant="text"
          disabled={pending}
          onClick={toggleStatus}
          iconLeft={{ name: 'play', variant: 'outline' }}
          label={t('rules-management.rules.rule.reactivate')}
          dataSelector="rules-management.rules.rule.reactivate"
        />
      )}
    </IpponHSpace>
  );
};

const RuleComponent = ({
  rule,
  canManageRules,
  onStatusChanged,
}: {
  rule: Rule;
  canManageRules: boolean;
  onStatusChanged: () => void;
}) => {
  const { t } = useTranslation('rules-management');
  const [collapsed, setCollapsed] = useState(true);
  const toggle = () => setCollapsed((collapsed) => !collapsed);
  const id = `rule-${rule.id.value}`;
  const isInactive = rule.status === RuleStatus.INACTIVE;
  const canToggleStatus = canManageRules && rule.organizationId.isPresent();

  return (
    <div
      className={clsx('ippon-toggle', { '-open': !collapsed })}
      data-selector="rules-management.rules.rule"
    >
      <div className="ippon-toggle--header">
        <button
          type="button"
          className="ippon-toggle--button"
          data-selector="rules-management.rules.rule.toggle"
          onClick={toggle}
          id={id}
          aria-expanded={!collapsed}
        >
          <IpponIon name="chevron-forward" className="ippon-toggle--icon" />
          <RuleHeadingComponent rule={rule} />
        </button>
        {(isInactive || canToggleStatus) && (
          <div className="ippon-toggle--actions">
            {isInactive && (
              <IpponBadge color="warning" dataSelector="rules-management.rules.rule.status">
                {t('rules-management.rules.rule.status.inactive')}
              </IpponBadge>
            )}
            {canToggleStatus && (
              <RuleStatusActionComponent rule={rule} onChanged={onStatusChanged} />
            )}
          </div>
        )}
      </div>
      {!collapsed && (
        <section aria-labelledby={id}>
          <div className="ippon-toggle--content">
            <IpponVSpace gap={8}>
              <RuleDetailsComponent rule={rule} />
            </IpponVSpace>
          </div>
        </section>
      )}
    </div>
  );
};

const RulesListResolvedComponent = ({
  page,
  canManageRules,
  onPageChange,
  onStatusChanged,
}: {
  page: Page<Rule>;
  canManageRules: boolean;
  onPageChange: (page: number) => void;
  onStatusChanged: () => void;
}) => {
  const { t } = useTranslation('rules-management');
  return (
    <IpponVSpace gap={8}>
      <IpponText variant="body" weight="bold" tag="h3" dataSelector="rules-management.rules.count">
        {t('rules-management.rules.count', { count: page.totalElementsCount })}
      </IpponText>
      {page.content.map((rule) => (
        <RuleComponent
          key={rule.id.value}
          rule={rule}
          canManageRules={canManageRules}
          onStatusChanged={onStatusChanged}
        />
      ))}
      <PaginationComponent
        currentPage={page.currentPage}
        totalPages={page.totalPages}
        onPageChange={onPageChange}
        dataSelector="rules-management.rules.pagination"
      />
    </IpponVSpace>
  );
};

const RULES_PAGE_SIZE = 30;

const RulesListComponent = ({ filter }: { filter: RulesFilter }) => {
  const [page, setPage] = useState(1);
  const [refreshes, setRefreshes] = useState(0);
  const canManageRules = useCanManageRules();

  const refresh = () => setRefreshes((refreshes) => refreshes + 1);

  useEffect(() => inject(RULES_REPOSITORY).onRulesPublished(refresh), []);

  const rulesLoader = useLoader(
    () => inject(RULES_REPOSITORY).list(Pagination.of({ page, pageSize: RULES_PAGE_SIZE }), filter),
    [page, refreshes, filter],
  );

  const onLoading = () => <RuleLoadingComponent />;

  const onError = (error: Error) => (
    <IpponContainer>
      <IpponCard shadow="l3">
        <LoadingErrorComponent error={error} dataSelector="rules-management.rules.error" />
      </IpponCard>
    </IpponContainer>
  );

  const onSuccess = (rulesPage: Page<Rule>) => (
    <RulesListResolvedComponent
      page={rulesPage}
      canManageRules={canManageRules}
      onPageChange={setPage}
      onStatusChanged={refresh}
    />
  );

  return (
    <LoaderComponent
      loader={rulesLoader}
      onSuccess={onSuccess}
      onLoading={onLoading}
      onError={onError}
    />
  );
};

const SEARCH_DEBOUNCE_MS = 300;

const SearchField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const { t } = useTranslation('rules-management');
  const [text, setText] = useState(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setText(next);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => onChange(next), SEARCH_DEBOUNCE_MS);
  };

  return (
    <IpponInputSearch
      value={text}
      onChange={handleChange}
      placeholder={t('rules-management.rules.search-placeholder')}
      dataSelector="rules-management.rules.search"
    />
  );
};

const SCOPE_KEYS = ['ALL', 'DEFAULT', 'ORGANIZATION'] as const;

type ScopeKey = (typeof SCOPE_KEYS)[number];

const useScopeLabel = () => {
  const { t } = useTranslation('rules-management');

  return (key: ScopeKey): string => {
    switch (key) {
      case 'ALL':
        return t('rules-management.rules.scope.all');
      case 'DEFAULT':
        return t('rules-management.rules.scope.default');
      case 'ORGANIZATION':
        return t('rules-management.rules.scope.organization');
    }
  };
};

const ScopeCombobox = ({
  scope,
  onChange,
  organizationId,
}: {
  scope: RulesScope;
  onChange: (scope: RulesScope) => void;
  organizationId: Optional<OrganizationId>;
}) => {
  const scopeLabel = useScopeLabel();
  const [query, setQuery] = useState('');

  const keys = organizationId.isPresent()
    ? SCOPE_KEYS
    : SCOPE_KEYS.filter((key) => key !== 'ORGANIZATION');

  const options = keys.map((key) => ({
    key,
    label: scopeLabel(key),
  }));
  const selection = options.find((option) => option.key === scope.kind);

  const handleSelect = (option: { key: ScopeKey }) => {
    setQuery('');
    switch (option.key) {
      case 'ALL':
        onChange(RulesScope.all());
        return;
      case 'DEFAULT':
        onChange(RulesScope.default());
        return;
      case 'ORGANIZATION':
        organizationId.ifPresent((id) => onChange(RulesScope.organization(id.value)));
    }
  };

  return (
    <IpponSingleCombobox
      id="rules-management-rules-scope"
      dataSelector="rules-management.rules.scope"
      labelledBy="rules-management-rules-scope-label"
      options={options}
      query={query}
      onQueryChange={setQuery}
      selection={selection}
      onSelect={handleSelect}
    />
  );
};

const ScopeField = ({
  scope,
  onChange,
}: {
  scope: RulesScope;
  onChange: (scope: RulesScope) => void;
}) => {
  const { t } = useTranslation('rules-management');
  const { injectUsersRepository } = initAccount();
  const userLoader = useLoader(() => injectUsersRepository().currentUser(), []);

  const hidden = () => null;

  const onUser = (user: User) =>
    user.organization.isPresent() ? (
      <IpponField>
        <IpponLabel htmlFor="rules-management-rules-scope" id="rules-management-rules-scope-label">
          {t('rules-management.rules.scope.label')}
        </IpponLabel>
        <ScopeCombobox
          scope={scope}
          onChange={onChange}
          organizationId={user.organization.map((organization) => organization.id)}
        />
      </IpponField>
    ) : null;

  const onCurrentUser = (currentUser: Result<WithoutOrganization, User>) =>
    currentUser.fold({ error: hidden, success: onUser });

  return (
    <LoaderComponent
      loader={userLoader}
      onLoading={hidden}
      onError={hidden}
      onSuccess={onCurrentUser}
    />
  );
};

const STATUS_KEYS = ['ALL', 'ACTIVE', 'INACTIVE'] as const;

type StatusKey = (typeof STATUS_KEYS)[number];

const useStatusLabel = () => {
  const { t } = useTranslation('rules-management');

  return (key: StatusKey): string => {
    switch (key) {
      case 'ALL':
        return t('rules-management.rules.status.all');
      case 'ACTIVE':
        return t('rules-management.rules.status.active');
      case 'INACTIVE':
        return t('rules-management.rules.status.inactive');
    }
  };
};

const statusKeyOf = (status: Optional<RuleStatus>): StatusKey =>
  status
    .map((value): StatusKey => (value === RuleStatus.ACTIVE ? 'ACTIVE' : 'INACTIVE'))
    .orElse('ALL');

const StatusCombobox = ({
  status,
  onChange,
}: {
  status: Optional<RuleStatus>;
  onChange: (status: Optional<RuleStatus>) => void;
}) => {
  const statusLabel = useStatusLabel();
  const [query, setQuery] = useState('');

  const options = STATUS_KEYS.map((key) => ({
    key,
    label: statusLabel(key),
  }));
  const selection = options.find((option) => option.key === statusKeyOf(status));

  const handleSelect = (option: { key: StatusKey }) => {
    setQuery('');
    switch (option.key) {
      case 'ALL':
        onChange(Optional.empty());
        return;
      case 'ACTIVE':
        onChange(Optional.of(RuleStatus.ACTIVE));
        return;
      case 'INACTIVE':
        onChange(Optional.of(RuleStatus.INACTIVE));
    }
  };

  return (
    <IpponSingleCombobox
      id="rules-management-rules-status"
      dataSelector="rules-management.rules.status"
      labelledBy="rules-management-rules-status-label"
      options={options}
      query={query}
      onQueryChange={setQuery}
      selection={selection}
      onSelect={handleSelect}
    />
  );
};

const StatusField = ({
  status,
  onChange,
}: {
  status: Optional<RuleStatus>;
  onChange: (status: Optional<RuleStatus>) => void;
}) => {
  const { t } = useTranslation('rules-management');
  const { injectUsersRepository } = initAccount();
  const userLoader = useLoader(() => injectUsersRepository().currentUser(), []);

  const hidden = () => null;

  const onUser = (user: User) =>
    user.organization.isPresent() ? (
      <IpponField>
        <IpponLabel
          htmlFor="rules-management-rules-status"
          id="rules-management-rules-status-label"
        >
          {t('rules-management.rules.status.label')}
        </IpponLabel>
        <StatusCombobox status={status} onChange={onChange} />
      </IpponField>
    ) : null;

  const onCurrentUser = (currentUser: Result<WithoutOrganization, User>) =>
    currentUser.fold({ error: hidden, success: onUser });

  return (
    <LoaderComponent
      loader={userLoader}
      onLoading={hidden}
      onError={hidden}
      onSuccess={onCurrentUser}
    />
  );
};

const SEVERITY_KEYS = ['ALL', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;

type SeverityKey = (typeof SEVERITY_KEYS)[number];

const severityKeyOf = (severity: Optional<Severity>): SeverityKey =>
  severity
    .map((value): SeverityKey => {
      switch (value) {
        case Severity.CRITICAL:
          return 'CRITICAL';
        case Severity.HIGH:
          return 'HIGH';
        case Severity.MEDIUM:
          return 'MEDIUM';
        case Severity.LOW:
          return 'LOW';
        case Severity.INFO:
          return 'ALL';
      }
    })
    .orElse('ALL');

const SeverityCombobox = ({
  severity,
  onChange,
}: {
  severity: Optional<Severity>;
  onChange: (severity: Optional<Severity>) => void;
}) => {
  const { t } = useTranslation('rules-management');
  const lowLabel = useSeverityText(Severity.LOW);
  const mediumLabel = useSeverityText(Severity.MEDIUM);
  const highLabel = useSeverityText(Severity.HIGH);
  const criticalLabel = useSeverityText(Severity.CRITICAL);
  const [query, setQuery] = useState('');

  const labelOf = (key: SeverityKey): string => {
    switch (key) {
      case 'ALL':
        return t('rules-management.rules.severity.all');
      case 'LOW':
        return lowLabel;
      case 'MEDIUM':
        return mediumLabel;
      case 'HIGH':
        return highLabel;
      case 'CRITICAL':
        return criticalLabel;
    }
  };

  const options = SEVERITY_KEYS.map((key) => ({ key, label: labelOf(key) }));
  const selection = options.find((option) => option.key === severityKeyOf(severity));

  const handleSelect = (option: { key: SeverityKey }) => {
    setQuery('');
    switch (option.key) {
      case 'ALL':
        onChange(Optional.empty());
        return;
      case 'LOW':
        onChange(Optional.of(Severity.LOW));
        return;
      case 'MEDIUM':
        onChange(Optional.of(Severity.MEDIUM));
        return;
      case 'HIGH':
        onChange(Optional.of(Severity.HIGH));
        return;
      case 'CRITICAL':
        onChange(Optional.of(Severity.CRITICAL));
    }
  };

  return (
    <IpponSingleCombobox
      id="rules-management-rules-severity"
      dataSelector="rules-management.rules.severity"
      labelledBy="rules-management-rules-severity-label"
      options={options}
      query={query}
      onQueryChange={setQuery}
      selection={selection}
      onSelect={handleSelect}
    />
  );
};

const SeverityField = ({
  severity,
  onChange,
}: {
  severity: Optional<Severity>;
  onChange: (severity: Optional<Severity>) => void;
}) => {
  const { t } = useTranslation('rules-management');

  return (
    <IpponField>
      <IpponLabel
        htmlFor="rules-management-rules-severity"
        id="rules-management-rules-severity-label"
      >
        {t('rules-management.rules.severity.label')}
      </IpponLabel>
      <SeverityCombobox severity={severity} onChange={onChange} />
    </IpponField>
  );
};

const RulesFilterComponent = ({
  filter,
  onChange,
}: {
  filter: RulesFilter;
  onChange: (filter: RulesFilter) => void;
}) => (
  <IpponHSpace gap={16} align="bottom">
    <IpponHSpaceSlot expand={true}>
      <SearchField
        value={filter.text.orElse('')}
        onChange={(text) => onChange({ ...filter, text: Optional.ofFalsifiable(text) })}
      />
    </IpponHSpaceSlot>
    <ScopeField scope={filter.scope} onChange={(scope) => onChange({ ...filter, scope })} />
    <StatusField status={filter.status} onChange={(status) => onChange({ ...filter, status })} />
    <SeverityField
      severity={filter.severity}
      onChange={(severity) => onChange({ ...filter, severity })}
    />
  </IpponHSpace>
);

export const RulesComponent = () => {
  const { t } = useTranslation('rules-management');
  const [filter, setFilter] = useState<RulesFilter>(RulesFilter.none);
  const [filterVersion, setFilterVersion] = useState(0);

  const { notifyPageMounted } = useRuleExtractionTracking();
  // Layout effect so the unmount cleanup runs synchronously in the same commit as the route
  // change, before any pending fetch (e.g. the draft rules load after a background extraction
  // completes) can read a stale "mounted" ref through a passive effect's deferred cleanup.
  useLayoutEffect(() => {
    notifyPageMounted(true);
    return () => notifyPageMounted(false);
  }, [notifyPageMounted]);

  const updateFilter = (updated: RulesFilter) => {
    setFilter(updated);
    setFilterVersion((version) => version + 1);
  };

  return (
    <IpponVSpace gap={32}>
      <IpponCard shadow="l3" size="large">
        <IpponVSpace gap={16}>
          <IpponHSpace align="middle" gap={16}>
            <IpponHSpaceSlot expand={true}>
              <IpponVSpace gap={4}>
                <IpponTitle tag="h2" level={5} dataSelector="rules-management.rules.title">
                  {t('rules-management.rules.title')}
                </IpponTitle>
                <IpponText
                  variant="body"
                  size="small"
                  color="neutral-tertiary"
                  dataSelector="rules-management.rules.description"
                >
                  {t('rules-management.rules.description')}
                </IpponText>
              </IpponVSpace>
            </IpponHSpaceSlot>
            <RulesImportTriggerComponent />
          </IpponHSpace>
          <RulesFilterComponent filter={filter} onChange={updateFilter} />
        </IpponVSpace>
      </IpponCard>
      <RulesListComponent key={filterVersion} filter={filter} />
    </IpponVSpace>
  );
};
